import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CompanyMission = () => {
  const differentiators = [
    {
      icon: 'Brain',
      title: 'AI-First Approach',
      description: 'We don\'t just implement AI; we think AI-first in everything we do'
    },
    {
      icon: 'Shield',
      title: 'Enterprise Security',
      description: 'Built-in cybersecurity expertise ensures your AI solutions are secure by design'
    },
    {
      icon: 'Zap',
      title: 'Rapid Deployment',
      description: 'Our proven methodologies deliver results 60% faster than industry standards'
    }
  ];

  return (
    <section className="relative">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
          <Icon name="Building2" size={16} />
          <span>About RCEG AI Ventures</span>
        </div>
        
        <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
          Transforming Enterprises Through 
          <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> AI Excellence</span>
        </h1>
        
        <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
          Since 2020, RCEG AI Ventures has been at the forefront of artificial intelligence innovation, 
          delivering cutting-edge solutions that drive measurable business transformation for enterprise clients worldwide.
        </p>
        
        <div className="flex items-center justify-center space-x-4">
          <Button 
            variant="default" 
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={() => window.location.href = '/contact-consultation-request'}
          >
            Partner with Us
          </Button>
          <Button 
            variant="outline" 
            size="lg"
            iconName="Users"
            iconPosition="left"
            onClick={() => window.location.href = '/team-profiles'}
          >
            Meet Our Team
          </Button>
        </div>
      </div>

      {/* Mission Statement */}
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8 lg:p-12 mb-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-4">Our Mission</h2>
          <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
            To democratize artificial intelligence by making advanced AI solutions accessible, secure, 
            and transformative for businesses of all sizes. We bridge the gap between cutting-edge AI research 
            and practical business applications, ensuring our clients stay ahead in an AI-driven world.
          </p>
        </div>
      </div>

      {/* Key Differentiators */}
      <div>
        <h3 className="text-2xl font-bold text-foreground text-center mb-8">What Sets Us Apart</h3>
        <div className="grid lg:grid-cols-3 gap-8">
          {differentiators?.map((item, index) => (
            <div key={index} className="text-center group">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full gradient-primary flex items-center justify-center group-hover:scale-110 transition-smooth">
                <Icon name={item?.icon} size={24} color="white" />
              </div>
              <h4 className="text-xl font-semibold text-foreground mb-2">{item?.title}</h4>
              <p className="text-muted-foreground">{item?.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Founding Story */}
      <div className="mt-16 bg-white rounded-2xl shadow-soft border border-border p-8 lg:p-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-2xl font-bold text-foreground mb-6">Our Story</h3>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Founded in 2020 by a team of AI researchers and enterprise consultants, 
                RCEG AI Ventures emerged from a simple observation: while AI technology was rapidly advancing, 
                most businesses struggled to harness its transformative potential.
              </p>
              <p>
                We set out to change that by creating a consultancy that combines deep technical expertise 
                with practical business acumen. Our multidisciplinary approach ensures that every AI solution 
                we deliver not only leverages the latest technology but also drives tangible business results.
              </p>
              <p>
                Today, we're proud to have helped over 50 enterprise clients across various industries 
                successfully implement AI solutions that have generated millions in value and operational efficiency.
              </p>
            </div>
          </div>
          
          <div className="relative">
            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-8">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">2020</div>
                  <div className="text-sm text-muted-foreground">Founded</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">50+</div>
                  <div className="text-sm text-muted-foreground">Enterprise Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">500+</div>
                  <div className="text-sm text-muted-foreground">Projects Delivered</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">98%</div>
                  <div className="text-sm text-muted-foreground">Client Retention</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyMission;